import { BECategory } from 'constants/categories';
import { imageUrlKey } from 'constants/rulebookVariables';
import { parseDollarsString } from 'models/dollars';
import { isMecnetEvent, type EventVo } from 'models/event';
import {
    type MarketStatus,
    type MarketVo,
    marketLastPriceCents,
    controversyScore,
    complementCents,
    subtractCents,
    midpointCents,
    marketVolumeContracts,
} from 'models/market';
import { getMinimalSubtitle } from 'utils/events';
import { getEventImageUrl, toCdnImageUrl } from 'utils/images';
import { type EventPricingInfo, resolveInverseTicker } from 'utils/marketPricing';
import { isMarketClosed, shrunkMarkets } from 'utils/markets';
import { type NumericTarget, computeNumericTarget } from 'utils/numericTarget';
import { isValidPriceDollars } from 'utils/price';

/**
 * Display helpers over plain market/event VOs.
 */

/**
 * Last price in cents, zeroed when invalid.
 */
function marketVoLastPriceCents(market: MarketVo): number {
    const dollars = parseDollarsString(market.last_price_dollars);
    return isValidPriceDollars(dollars) ? dollars.times(100).toNumber() : 0;
}

/** Yes bid in cents, zeroed when invalid. */
function marketVoYesBidCents(market: MarketVo): number {
    const dollars = parseDollarsString(market.yes_bid_dollars);
    return isValidPriceDollars(dollars) ? dollars.times(100).toNumber() : 0;
}

/** Yes ask in cents, defaulting to 100 when invalid. */
function marketVoYesAskCents(market: MarketVo): number {
    const dollars = parseDollarsString(market.yes_ask_dollars);
    return isValidPriceDollars(dollars) ? dollars.times(100).toNumber() : 100;
}

/**
 * Last traded yes price, otherwise tight-spread mid or 50.
 */
function marketVoLastValidYesPrice(market: MarketVo): number {
    const SPREAD_LIMIT = 30;
    const yesBid = marketVoYesBidCents(market);
    const yesAsk = marketVoYesAskCents(market);
    const spread = Math.abs(subtractCents(yesBid, yesAsk));
    const midSpread = Math.floor(midpointCents(yesBid, yesAsk));
    const defaultPrice = spread < SPREAD_LIMIT ? midSpread : 50;
    const lastPrice = marketVoLastPriceCents(market);
    return 0 < lastPrice && lastPrice < 100 ? lastPrice : defaultPrice;
}

/**
 * Pricing inputs from event VOs; market order stays in API response order.
 */
function toEventPricingInfo(event: EventVo): EventPricingInfo {
    return {
        markets: event.markets.map((m) => ({
            ticker: m.ticker_name,
            lastPriceCents: marketVoLastPriceCents(m),
            volume: marketVolumeContracts(m),
        })),
        mutuallyExclusive: isMecnetEvent(event),
        isSportsCategory: event.category === BECategory.SPORTS,
    };
}

/** Market probability with optional inverse-market normalization. */
export function marketVoProbability(market: MarketVo, event?: EventVo): number {
    if (market.result === 'yes') return 100;
    if (market.result === 'no') return 0;

    if (event) {
        const inverseTicker = resolveInverseTicker(market.ticker_name, toEventPricingInfo(event));
        if (inverseTicker) {
            const inverseMarket = event.markets.find((m) => m.ticker_name === inverseTicker);
            return complementCents(inverseMarket ? marketVoLastValidYesPrice(inverseMarket) : 0);
        }
    }

    return marketVoLastValidYesPrice(market);
}

/**
 * Settled status from fetched MarketVo fields.
 */
export function marketVoIsSettled(market: Pick<MarketVo, 'status'>): boolean {
    return market.status === 'settled' || market.status === 'finalized';
}

/**
 * Determined status from fetched MarketVo fields.
 */
export function marketVoIsDetermined(market: Pick<MarketVo, 'status' | 'result'>): boolean {
    return (
        market.status === 'determined' ||
        market.status === 'disputed' ||
        market.status === 'amended' ||
        marketVoIsSettled(market) ||
        !!market.result
    );
}

/**
 * Closed/paused status, determination, or a past close date.
 */
export function marketVoIsClosed(market: Pick<MarketVo, 'status' | 'result' | 'close_date'>): boolean {
    return (
        market.status === 'closed' ||
        market.status === 'inactive' ||
        marketVoIsDetermined(market) ||
        isMarketClosed(market.close_date)
    );
}

/** Market subtitle fallback. */
export function marketVoSubtitle(market: MarketVo): string {
    return market.yes_sub_title || market.sub_title || '';
}

/**
 * Event-sourced display subtitle for one of the event's markets, or `''` when
 * the event payload isn't available or doesn't carry the market.
 *
 * `/v1/events...` payloads apply the partner-league official-name override
 * (`official_name_title` product metadata) into each market's `yes_sub_title`
 * server-side (e.g. "Baltimore Ravens"). The market-level endpoints
 * (`/markets/{id}`, `/markets_by_ticker/{ticker}` and their `/cached`
 * variants) do NOT — they return the raw broker-safe city name ("Baltimore")
 * and carry no `product_metadata` to re-derive the official name from. Any
 * surface that renders a market name from a market-level payload should
 * prefer this whenever the event payload is already on hand, so the displayed
 * name doesn't flip with whichever source resolved last; callers keep their
 * market-payload fallback for when it isn't.
 */
export function eventVoMarketSubtitle(
    event: EventVo | null | undefined,
    marketTicker: string | null | undefined,
): string {
    if (!event?.markets || !marketTicker) return '';
    const market = event.markets.find((m) => m.ticker_name === marketTicker);
    return market ? marketVoSubtitle(market) : '';
}

/** Numeric target from fetched MarketVo fields, mirroring `BinaryMarket.numericTarget`. */
function marketVoNumericTarget(market: MarketVo): NumericTarget | null {
    return computeNumericTarget({
        subtitle: marketVoSubtitle(market),
        strikeType: market.strike_type ?? '',
        rulebookVariables: market.rulebook_variables ?? {},
        floorStrike: market.floor_strike,
        capStrike: market.cap_strike,
    });
}

/** Market image from rulebook variable. */
export function marketVoImageUrl(market: MarketVo): string | null {
    const imagePath = (market.rulebook_variables ?? {})[imageUrlKey];
    if (!imagePath) {
        return null;
    }
    return toCdnImageUrl(imagePath, 'xs');
}

/** Minimal event subtitle. */
export function eventVoMinimalSubTitle(event: EventVo): string {
    return getMinimalSubtitle(event.sub_title.trim());
}

/** Event title with markdown emphasis removed. */
export function eventVoTitle(event: EventVo): string {
    return event.title.replace(/\*\*/g, '');
}

/** Event image, preferring custom product metadata. */
export function eventVoImageUrl(event: EventVo): string {
    const customUrl = event.product_metadata?.custom_image_url;
    return getEventImageUrl({
        seriesTicker: event.series_ticker,
        customImageUrl: typeof customUrl === 'string' ? customUrl : undefined,
    });
}

/**
 * True binary when there is one market and no numeric target.
 */
export function eventVoIsTrueBinary(event: EventVo): boolean {
    if (event.markets.length !== 1) {
        return false;
    }
    return marketVoNumericTarget(event.markets[0]) === null;
}

/**
 * Most active status for a materialized event VO.
 */
function eventVoMostActiveStatus(event: EventVo): MarketStatus {
    const statuses = new Set(event.markets.map((m) => m.status));
    const statusPriorities: MarketStatus[] = [
        'active',
        'initialized',
        'closed',
        'determined',
        'amended',
        'disputed',
        'settled',
        'finalized',
        'inactive',
        'invalid',
    ];

    for (const status of statusPriorities) {
        if (statuses.has(status)) {
            return status;
        }
    }
    return 'settled';
}

/** Event settled when its most active status is settled/finalized. */
export function eventVoIsSettled(event: EventVo): boolean {
    const status = eventVoMostActiveStatus(event);
    return status === 'settled' || status === 'finalized';
}

/**
 * Mirrors `Event.isParsableNumeric`: every market must have a numeric target
 * AND the targets must vary across markets (rules out temporal series like
 * `KXBTCMAX100` where every market shares the same `floor_strike` and the
 * differentiator is the date).
 */
export function eventVoIsParsableNumeric(event: EventVo): boolean {
    const markets = event.markets;
    if (markets.length <= 1) return false;
    const targets = markets.map(marketVoNumericTarget);
    if (!targets.every((t) => t !== null)) return false;
    const lowerBounds = new Set(targets.map((t) => t?.lowerBound ?? null));
    const upperBounds = new Set(targets.map((t) => t?.upperBound ?? null));
    if (lowerBounds.size === 1 && upperBounds.size === 1) return false;
    return true;
}

/**
 * Mirrors the `Event.markets` getter ordering: keep the response order when
 * every market subtitle carries an implicit ordering (digits or month names),
 * otherwise sort by price descending; undetermined markets always sort first.
 * The settled winner-to-top pass is omitted — it needs settlement state that
 * only matters for fully-determined events.
 */
function eventVoOrderedMarkets(event: EventVo): MarketVo[] {
    const implicitOrdering = event.markets.every((m) =>
        /\d|(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/.test(marketVoSubtitle(m)),
    );
    const byPriceDescThenTicker = (a: MarketVo, b: MarketVo): number =>
        marketLastPriceCents(b) - marketLastPriceCents(a) || a.ticker_name.localeCompare(b.ticker_name);
    const undeterminedFirst = (a: MarketVo, b: MarketVo): number => {
        if (marketVoIsDetermined(a) === marketVoIsDetermined(b)) return 0;
        return marketVoIsDetermined(a) ? 1 : -1;
    };
    return event.markets
        .slice()
        .sort((a, b) => (implicitOrdering ? 0 : byPriceDescThenTicker(a, b)))
        .sort(undeterminedFirst);
}

/**
 * Mirrors `BinaryMarket.controversy`'s mutually-exclusive branch: ranks the
 * highest-yes-bid outcome first (the favorite leads the ladder), rather than
 * the balanced-near-50% ranking the plain `controversy` util (used by
 * `shrunkMarkets`) applies to independent-outcome events. Needed for
 * categorical events (election/seat-range ladders) where one outcome is a
 * strong favorite — the plain score penalizes prices above 50 and would
 * surface underdogs instead.
 */
function marketVoMutuallyExclusiveControversy(market: MarketVo): number {
    if (marketVoIsDetermined(market)) return 0;
    return controversyScore(
        {
            yesBid: marketVoYesBidCents(market),
            yesAsk: marketVoYesAskCents(market),
            lastPrice: marketVoLastPriceCents(market),
        },
        true,
    );
}

/**
 * Plain-VO mirror of `queries/marketEntityTransform.ts`'s `eventBaseTopMarkets`
 * no-`highlightedMarkets` path, for consumers that fetch events via react-query
 * instead of a live entity store.
 * Numeric-ladder events keep their response order (matching the domain path,
 * where `mostControversialChunk` over the full market list is the identity);
 * categorical events surface the most controversial markets, favorite-first
 * when mutually exclusive. The sports tie-market filtering is omitted — it
 * needs `series.isSports`, which a plain `EventVo` doesn't carry.
 */
export function eventVoBaseTopMarkets(event: EventVo): MarketVo[] {
    if (event.ticker === 'KXBALANCEPOWERCOMBO-27FEB') {
        return event.markets;
    }

    const ordered = eventVoOrderedMarkets(event);
    const marketsToSelect = eventVoIsParsableNumeric(event)
        ? ordered
        : isMecnetEvent(event)
          ? [...ordered].sort(
                (a, b) => marketVoMutuallyExclusiveControversy(b) - marketVoMutuallyExclusiveControversy(a),
            )
          : shrunkMarkets([...ordered], ordered.length);
    const maxMarkets = event.category === BECategory.SPORTS ? 2 : 3;

    return marketsToSelect
        .slice(0, maxMarkets)
        .filter((m) => (marketLastPriceCents(m) ?? 0) > 0)
        .sort((a, b) => ordered.indexOf(a) - ordered.indexOf(b));
}
